import React, { useState, useEffect, useRef } from "react";
import { container } from "../styles/commonStyles";
import StreakFeedbackModal from "../components/StreakFeedbackModal";
import { playSuccessSound } from "../utils/soundManager";

const generateShareText = (gameType, score, playMode) => {
  let gameName = "Vocab Game";
  let emoji = "🧠";
  let hook = "Can you beat my score?";

  if (gameType === "meaning") {
    gameName = "Meaning Match";
    emoji = "🧠";
    hook = "Can you beat me?";
  } else if (gameType === "visualization") {
    gameName = "Visualization";
    emoji = "👁️";
    hook = "Train your word imagination.";
  } else if (gameType === "connotation") {
    gameName = "Connotations";
    emoji = "🎯";
    hook = "Can you feel the nuance?";
  } else if (gameType === "mixed") {
    gameName = "Mixed Practice";
    emoji = "🔥";
    hook = "Ready for the ultimate challenge?";
  }

  const modeStr = playMode === "quick" ? "Quick" : playMode === "learn" ? "Learning" : "Standard";

  return `I scored ${score} in ${gameName} (${modeStr}) on Vocab Game ${emoji}\n${hook}`;
};

const ResultScreen = ({
  theme,
  words,
  gameType,
  gameLength,
  scoreHistoryMap,
  score,
  user,
  goHome,
  currentIndex,
  speedyCount,
  setStep,
  playMode,
  dbHistory,
  isHistoryLoading,
  dbBestScore,
  streakFeedback,
  setStreakFeedback,
  lives,
  workoutReward
}) => {
  const [filter, setFilter] = useState("all");
  const [isTrendOpen, setIsTrendOpen] = useState(false);
  const isDark = theme === "dark";

  const colors = {
    background: isDark ? "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)" : "linear-gradient(180deg, #E0F2F1 0%, #F8FAFC 100%)",
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#0F172A",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    border: isDark ? "rgba(255, 255, 255, 0.08)" : "#F1F5F9",
    accentSurface: isDark ? "rgba(20, 184, 166, 0.1)" : "#F0FDFA",
    chartGrid: isDark ? "#334155" : "#F1F5F9"
  };

  const resultSoundPlayed = useRef(false);

  useEffect(() => {
    // Play exactly once when Result screen mounts, regardless of score/performance
    if (!resultSoundPlayed.current) {
      resultSoundPlayed.current = true;
      playSuccessSound();
    }
  }, []);

  const handleShare = async () => {
    const text = generateShareText(gameType, score, playMode);

    // 1. Synchronously copy to clipboard FIRST.
    // This guarantees we capture the user gesture before any `await` calls (like navigator.share) consume it.
    let syncCopied = false;
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      // Make it invisible but keep it in the viewport to satisfy browser security
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = "0";
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";
      textArea.style.color = "transparent";

      document.body.appendChild(textArea);

      // Standard selection
      textArea.focus();
      textArea.select();

      // iOS specific selection workaround
      try {
        textArea.setSelectionRange(0, 999999);
      } catch (e) { }

      syncCopied = document.execCommand('copy');
      document.body.removeChild(textArea);
    } catch (err) {
      console.error("execCommand failed:", err);
    }

    // 2. Try Native Share
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Vocab Game Score',
          text: text,
        });
        return; // Success, user shared natively!
      } catch (err) {
        if (err.name === 'AbortError') {
          return; // User intentionally cancelled native share
        }
        console.error("Native share failed:", err);
        // Fall through to alert the user that we at least copied it
      }
    }

    // 3. If execCommand failed, try modern Clipboard API (Requires HTTPS or localhost)
    if (!syncCopied && navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        syncCopied = true;
      } catch (err) {
        console.error("Clipboard API failed:", err);
      }
    }

    // 4. Alert success or show manual prompt
    if (syncCopied) {
      alert("Score copied to clipboard! Ready to share.");
    } else {
      window.prompt("Copy your score to share:", text);
    }
  };

  const attempted = words.filter(w => w.status === "correct" || w.status === "wrong").length;
  const correctCount = words.filter(w => w.status === "correct").length;
  const wrongCount = words.filter(w => w.status === "wrong").length;
  const accuracy = attempted ? Math.round((correctCount / attempted) * 100) : 0;

  // Filter words based on selected tab
  const filteredWords = words.filter(w => {
    if (filter === "all") return true;
    return w.status === filter;
  });

  // Trend analysis data - Prefer database history as source of truth
  const sessionDataObj = (gameType && gameLength) ? scoreHistoryMap[`${gameType}_${gameLength}`] : null;
  const historyPayloads = (dbHistory && dbHistory.length > 0) ? dbHistory : (Array.isArray(sessionDataObj) ? sessionDataObj : (sessionDataObj?.history || []));

  const runSize = 5;
  let thisRunScores = historyPayloads.map(h => h.score);

  // If dbHistory hasn't loaded yet, the database sync is still happening in the background.
  // We immediately inject the current score to guarantee the chart's latest point matches the top card.
  if (!dbHistory || dbHistory.length === 0) {
    thisRunScores.push(score);
  }

  thisRunScores = thisRunScores.slice(-runSize);


  return (
    <div style={{
      ...container,
      justifyContent: "flex-start",
      padding: "0",
      height: "auto",
      minHeight: "100vh",
      overflowY: "auto",
      background: colors.background
    }}>
      <StreakFeedbackModal theme={theme} streakFeedback={streakFeedback} setStreakFeedback={setStreakFeedback} />
      {/* TOP BAR */}
      <div style={{ width: "100%", maxWidth: "420px", padding: "16px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button
          onClick={goHome}
          style={{
            width: "32px", height: "32px", borderRadius: "50%",
            background: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.6)", border: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: colors.text,
            backdropFilter: "blur(4px)"
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>


      </div>

      {/* HERO SECTION */}
      <div style={{ width: "100%", maxWidth: "420px", padding: "24px 24px 20px", position: "relative" }}>
        <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: colors.text, lineHeight: "1.2" }}>
          Awesome Work,
        </h1>
        <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "800", color: "#14B8A6", lineHeight: "1.2" }}>
          {user?.displayName || user?.name || user?.username || "Learner"}!
        </h1>

        {/* Brain Asset */}
        <div style={{ position: "absolute", right: "0px", top: "-40px", width: "180px", height: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src="/images/game_s/result_brain.webp" alt="Brain" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
      </div>

      {/* NEW IMAGE-BASED SCORE CARD */}
      {(() => {
        const sessionDataObj = (gameType && playMode) ? scoreHistoryMap[`${gameType}_${playMode}`] : null;
        const historyPayloadsForNewBest = Array.isArray(sessionDataObj) ? sessionDataObj : (sessionDataObj?.history || []);
        const currentScoreValue = score;
        const previousBest = Math.max(
          dbBestScore || 0,
          historyPayloadsForNewBest.length > 1 ? Math.max(...historyPayloadsForNewBest.slice(0, -1).map(h => h.score)) : 0
        );

        const isNewBest = currentScoreValue > previousBest && previousBest > 0;

        return (
          <div style={{ width: "100%", maxWidth: "420px", padding: "0 12px", marginTop: "32px" }}>
            <div style={{
              background: colors.surface,
              borderRadius: "28px",
              padding: "16px 20px",
              boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.25)" : "0 10px 30px rgba(15,23,42,0.06)",
              position: "relative",
              border: isDark ? "1px solid #334155" : "1px solid #FFFFFF"
            }}>
              {/* Top Row: Trophy, Score, Accuracy */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", gap: "10px" }}>
                {/* Left: Trophy */}
                <div style={{ flex: "0 0 104px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img
                    src="/images/game_s/trophy.webp"
                    alt="Trophy"
                    style={{ width: "104px", height: "auto", objectFit: "contain", filter: "drop-shadow(0 8px 15px rgba(0,0,0,0.1))" }}
                  />
                </div>

                {/* Center: Score */}
                <div style={{ flex: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ fontSize: "10px", fontWeight: "700", color: colors.secondaryText, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "2px" }}>Your Score</div>
                  <div style={{ fontSize: "44px", fontWeight: "900", color: colors.text, lineHeight: "1" }}>{score.toLocaleString()}</div>
                  <div style={{
                    marginTop: "8px", background: isDark ? "rgba(16, 185, 129, 0.15)" : "#F0FDF4", padding: "4px 10px", borderRadius: "100px",
                    display: "flex", alignItems: "center", gap: "4px", border: isDark ? "1px solid rgba(20, 184, 166, 0.2)" : "1px solid rgba(74,222,128,0.2)"
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#14B8A6"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#14B8A6" }}>{isNewBest ? "New Best!" : "Good Job!"}</span>
                  </div>
                </div>

                {/* Right: Accuracy */}
                <div style={{ flex: "0 0 90px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderLeft: isDark ? "1px solid #334155" : "1px solid #E2E8F0", paddingLeft: "10px" }}>
                  <div style={{ position: "relative", width: "56px", height: "56px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="56" height="56" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke={isDark ? "#334155" : "#F1F5F9"} strokeWidth="10" />
                      <circle
                        cx="50" cy="50" r="42" fill="none" stroke="#14B8A6" strokeWidth="10"
                        strokeDasharray={2 * Math.PI * 42}
                        strokeDashoffset={2 * Math.PI * 42 * (1 - accuracy / 100)}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <div style={{ position: "absolute", fontSize: "14px", fontWeight: "800", color: colors.text }}>{accuracy}<span style={{ fontSize: "8px" }}>%</span></div>
                  </div>
                  <div style={{ marginTop: "4px", textAlign: "center" }}>
                    <div style={{ fontSize: "11px", fontWeight: "800", color: "#14B8A6" }}>{accuracy >= 70 ? "Great Job!" : "Keep At It!"}</div>
                  </div>
                </div>
              </div>

              {/* Bottom Row: Stats Container */}
              <div style={{ background: isDark ? "#0F172A" : "#FFFFFF", borderRadius: "20px", padding: "12px", display: "flex", gap: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" }}>
                <div style={{ flex: 1, background: isDark ? "rgba(74, 222, 128, 0.1)" : "#F0FDF4", borderRadius: "14px", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  <div style={{ width: "24px", height: "24px", background: "#4ADE80", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "15px", fontWeight: "800", color: colors.text, lineHeight: "1" }}>{correctCount}</span>
                    <span style={{ fontSize: "8px", fontWeight: "700", color: colors.secondaryText, textTransform: "uppercase" }}>Correct</span>
                  </div>
                </div>

                <div style={{ flex: 1, background: isDark ? "rgba(248, 113, 113, 0.1)" : "#FEF2F2", borderRadius: "14px", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  <div style={{ width: "24px", height: "24px", background: "#F87171", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "15px", fontWeight: "800", color: colors.text, lineHeight: "1" }}>{wrongCount}</span>
                    <span style={{ fontSize: "8px", fontWeight: "700", color: colors.secondaryText, textTransform: "uppercase" }}>Wrong</span>
                  </div>
                </div>

                <div style={{ flex: 1, background: isDark ? "rgba(251, 191, 36, 0.1)" : "#FFFBEB", borderRadius: "14px", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  <div style={{ width: "24px", height: "24px", background: "#FBBF24", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: "14px" }}>⚡</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "15px", fontWeight: "800", color: colors.text, lineHeight: "1" }}>{speedyCount}</span>
                    <span style={{ fontSize: "8px", fontWeight: "700", color: colors.secondaryText, textTransform: "uppercase" }}>Speedy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* PERFORMANCE TREND CARD */}
      <div style={{ width: "100%", maxWidth: "420px", padding: "0 12px", marginTop: "12px" }}>
        <div
          onClick={() => setIsTrendOpen(!isTrendOpen)}
          style={{
            background: colors.surface, borderRadius: "24px",
            padding: isTrendOpen ? "20px" : "12px 20px",
            boxShadow: isDark ? "0 10px 25px rgba(0,0,0,0.2)" : "0 10px 25px rgba(15,23,42,0.05)", cursor: "pointer",
            transition: "all 0.3 cubic-bezier(0.4, 0, 0.2, 1)",
            border: isDark ? "1px solid #334155" : "none"
          }}
        >
          {/* HEADER */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isTrendOpen ? "20px" : "0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div style={{
                width: isTrendOpen ? "44px" : "38px",
                height: isTrendOpen ? "44px" : "38px",
                background: isDark ? "rgba(16, 185, 129, 0.1)" : "#F0FDF4", borderRadius: isTrendOpen ? "12px" : "10px",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                transition: "all 0.3s"
              }}>
                <svg width={isTrendOpen ? "24" : "20"} height={isTrendOpen ? "24" : "20"} viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
              </div>
              <div>
                <div style={{
                  fontSize: isTrendOpen ? "20px" : "17px",
                  fontWeight: "800", color: colors.text,
                  lineHeight: "1.2", transition: "all 0.3s"
                }}>Trend Analysis</div>
              </div>
            </div>

            <div style={{
              width: "32px", height: "32px", background: isDark ? "#0F172A" : "#FFFFFF", borderRadius: "8px", border: isDark ? "1px solid #334155" : "1px solid #E2E8F0",
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.secondaryText} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isTrendOpen ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.3s" }}><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
          </div>

          {/* EXPANDABLE TREND CONTENT */}
          {isTrendOpen && (() => {
            if (isHistoryLoading) {
              return (
                <div style={{ height: "200px", display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                  <div style={{ width: "24px", height: "24px", border: `3px solid ${isDark ? "#334155" : "#F3F3F3"}`, borderTop: "3px solid #10B981", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
              );
            }
            if (thisRunScores.length < 1) return (
              <div style={{ padding: "20px", textAlign: "center", color: colors.secondaryText, fontSize: "14px" }}>
                Play more games to see your progress!
              </div>
            );


            const maxScoreVal = Math.max(...thisRunScores);
            const localBest = Array.isArray(sessionDataObj) ? Math.max(...sessionDataObj.map(h => h.score), 0) : (sessionDataObj?.best || 0);
            const historyBest = historyPayloads.length > 0 ? Math.max(...historyPayloads.map(h => h.score)) : 0;
            const bestScoreOverall = Math.max(localBest, historyBest, score, dbBestScore || 0);


            const chartMax = Math.ceil((maxScoreVal * 1.25) / 100) * 100 || 1000;


            const getChartY = (s) => 70 - (s / chartMax) * 60;
            const getChartX = (idx) => 15 + (idx * 18.75);

            const getBezier = (scores) => {
              if (scores.length < 2) return "";
              let d = `M ${getChartX(0)} ${getChartY(scores[0])}`;
              for (let i = 0; i < scores.length - 1; i++) {
                const x1 = getChartX(i), y1 = getChartY(scores[i]);
                const x2 = getChartX(i + 1), y2 = getChartY(scores[i + 1]);
                const cp1x = x1 + (x2 - x1) / 2;
                d += ` C ${cp1x} ${y1}, ${cp1x} ${y2}, ${x2} ${y2}`;
              }
              return d;
            };

            const scorePath = getBezier(thisRunScores);

            return (
              <div style={{ animation: "fadeIn 0.3s ease-out", width: "100%" }}>
                {/* CHART CONTAINER */}
                <div style={{
                  width: "100%",
                  height: "300px",
                  position: "relative",
                  marginBottom: "24px",
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <svg width="100%" height="100%" viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet">
                    <defs>
                      <linearGradient id="chartGrad" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="rgba(16,185,129,0.15)" />
                        <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                      </linearGradient>
                    </defs>

                    {/* Y-AXIS GRID & LABELS */}
                    {[0, chartMax * 0.33, chartMax * 0.66, chartMax].map(v => (
                      <g key={v}>
                        <line x1="12" y1={getChartY(v)} x2="95" y2={getChartY(v)} stroke={colors.chartGrid} strokeWidth="0.6" />
                        <text x="10" y={getChartY(v) + 1} fontSize="2.8" fill={colors.secondaryText} textAnchor="end" fontWeight="600">{Math.round(v).toLocaleString()}</text>
                      </g>
                    ))}

                    {/* DATA PATH */}
                    {thisRunScores.length >= 2 && (
                      <>
                        <path d={`${scorePath} L ${getChartX(thisRunScores.length - 1)} 70 L ${getChartX(0)} 70 Z`} fill="url(#chartGrad)" />
                        <path d={scorePath} fill="none" stroke="#10B981" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                      </>
                    )}

                    {/* DATA POINTS & TOOLTIPS */}
                    {thisRunScores.map((s, i) => {
                      const px = getChartX(i);
                      const py = getChartY(s);
                      const isLast = i === thisRunScores.length - 1;
                      return (
                        <g key={i}>
                          <rect x={px - 4.5} y={py - 10} width="9" height="6" rx="1.5" fill={isLast ? "#10B981" : (isDark ? "#0F172A" : "white")} style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.05))" }} />
                          <text x={px} y={py - 6} fontSize="2.8" fill={isLast ? "white" : colors.text} fontWeight="800" textAnchor="middle">{s.toLocaleString()}</text>
                          <circle cx={px} cy={py} r="1.8" fill={isDark ? "#0F172A" : "white"} stroke="#10B981" strokeWidth="0.8" />
                          <text x={px} y="77" fontSize="3.5" fill={colors.secondaryText} fontWeight="600" textAnchor="middle">{i + 1}</text>
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* BOTTOM STAT CARDS */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  width: "100%",
                  height: "auto",
                  flexShrink: 0
                }}>
                  {/* THIS RUN CARD */}
                  <div style={{ background: isDark ? "rgba(16, 185, 129, 0.1)" : "#F0FDF4", borderRadius: "20px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px", minWidth: 0, border: isDark ? "1px solid rgba(20, 184, 166, 0.2)" : "none" }}>
                    <div style={{ width: "36px", height: "36px", background: isDark ? "rgba(20, 184, 166, 0.2)" : "#DCFCE7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "10px", fontWeight: "800", color: "#10B981", textTransform: "uppercase" }}>This Run</div>
                      <div style={{ fontSize: "18px", fontWeight: "900", color: "#10B981", lineHeight: "1.1" }}>{score.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* BEST SCORE CARD */}
                  <div style={{ background: isDark ? "rgba(245, 158, 11, 0.1)" : "#FFF7ED", borderRadius: "20px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px", minWidth: 0, border: isDark ? "1px solid rgba(245, 158, 11, 0.2)" : "none" }}>
                    <div style={{ width: "36px", height: "36px", background: isDark ? "rgba(245, 158, 11, 0.2)" : "#FFEDD5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "10px", fontWeight: "800", color: "#F59E0B", textTransform: "uppercase" }}>Best Score</div>
                      <div style={{ fontSize: "18px", fontWeight: "900", color: "#F59E0B", lineHeight: "1.1" }}>{bestScoreOverall.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* DAILY WORKOUT / GUIDANCE AREA */}
      {(() => {

        const successful_games_count = user.successfulGamesCount || 0;

        const questionReached = currentIndex + 1;
        const threshold = gameLength === "quick" ? 7 : 12;
        const isValidGame = questionReached >= threshold;

        let messageType = "none";
        if (!isValidGame) {
          messageType = (successful_games_count === 0) ? "streak_guidance" : "workout_guidance";
        }

        if (successful_games_count > 3 && isValidGame) return null;
        if (successful_games_count >= 3 && !isValidGame) return null;

        if (!isValidGame) {
          const isStreak = messageType === "streak_guidance";
          return (
            <div style={{ width: "100%", maxWidth: "420px", padding: "0 12px", marginTop: "12px" }}>
              <div style={{
                background: colors.surface,
                borderRadius: "24px",
                padding: "20px 24px",
                boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.2)" : "0 10px 30px rgba(15,23,42,0.06)",
                border: isDark ? "1px solid #334155" : "1px solid #F1F5F9",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                position: "relative",
                overflow: "hidden"
              }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0, width: "4px",
                  background: isStreak ? "#EF4444" : "#3B82F6"
                }} />

                <div style={{
                  width: "48px", height: "48px",
                  background: isStreak ? (isDark ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2") : (isDark ? "rgba(59, 130, 246, 0.15)" : "#EFF6FF"),
                  borderRadius: "14px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "24px",
                  flexShrink: 0
                }}>
                  {isStreak ? "🔥" : "🎯"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: colors.text }}>
                      {isStreak ? "Streak Protection" : "Daily Workout"}
                    </div>
                    <span style={{
                      fontSize: "9px", fontWeight: "900", color: isStreak ? "#EF4444" : "#3B82F6",
                      background: isStreak ? (isDark ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2") : (isDark ? "rgba(59, 130, 246, 0.15)" : "#EFF6FF"),
                      padding: "3px 8px", borderRadius: "99px", textTransform: "uppercase", letterSpacing: "0.5px"
                    }}>
                      Next Time
                    </span>
                  </div>
                  <div style={{ fontSize: "13px", color: colors.secondaryText, fontWeight: "600", lineHeight: "1.4" }}>
                    {isStreak
                      ? `Reach Question ${threshold} to keep your streak alive! 💪`
                      : `Complete ${threshold} questions to count toward your workout. 🚀`
                    }
                  </div>
                </div>
              </div>
            </div>
          );
        }

        const valueUsedForWorkout = successful_games_count;

        return (
          <div style={{ width: "100%", maxWidth: "420px", padding: "0 12px", marginTop: "12px" }}>
            <div style={{
              background: colors.surface,
              borderRadius: "28px",
              padding: "20px 24px",
              boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.2)" : "0 10px 30px rgba(15,23,42,0.08)",
              border: isDark ? "1px solid #334155" : "1px solid #F1F5F9",
              display: "flex",
              flexDirection: "column",
              gap: "16px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ fontSize: "20px" }}>🏆</div>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: colors.text, letterSpacing: "-0.3px" }}>Daily Workout</div>
                </div>
                <div style={{
                  background: isDark ? "#0F172A" : "#F1F5F9",
                  padding: "6px 12px",
                  borderRadius: "12px",
                  fontSize: "13px",
                  fontWeight: "800",
                  color: colors.secondaryText,
                  letterSpacing: "0.5px"
                }}>
                  {Math.min(valueUsedForWorkout, 3)} / 3 <span style={{ fontSize: "10px", opacity: 0.8 }}>DONE</span>
                </div>
              </div>

              {valueUsedForWorkout >= 3 ? (
                <div style={{
                  background: isDark ? "rgba(16, 185, 129, 0.15)" : "#F0FDF4",
                  borderRadius: "16px",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  border: isDark ? "1px solid rgba(16, 185, 129, 0.2)" : "1px solid #DCFCE7"
                }}>
                  <div style={{
                    width: "32px", height: "32px",
                    background: "#10B981", borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "14px", fontWeight: "700", color: isDark ? "#10B981" : "#065F46", lineHeight: "1.3" }}>
                      Awesome work! You completed today's workout!
                    </div>
                    {workoutReward > 0 && (
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "800",
                        color: "#F59E0B",
                        marginTop: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}>
                        <span>🪙</span> +25 Coins Earned
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", padding: "0 10px" }}>
                  {[0, 1, 2].map((idx) => {
                    const isDone = idx < valueUsedForWorkout;
                    const isActive = idx === valueUsedForWorkout;
                    const isLocked = idx > valueUsedForWorkout;

                    return (
                      <React.Fragment key={idx}>
                        <div style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isDone ? "#10B981" : isActive ? "#3B82F6" : (isDark ? "#0F172A" : "#F1F5F9"),
                          boxShadow: isActive ? "0 0 15px rgba(59,130,246,0.4)" : "none",
                          transition: "all 0.3s ease",
                          flexShrink: 0
                        }}>
                          {isDone ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                          ) : isLocked ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.secondaryText} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                          ) : (
                            <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: "white" }}></div>
                          )}
                        </div>
                        {idx < 2 && (
                          <div style={{
                            flex: 1,
                            height: "4px",
                            background: idx < valueUsedForWorkout ? "#10B981" : (isDark ? "#0F172A" : "#F1F5F9"),
                            margin: "0 8px",
                            borderRadius: "2px",
                            transition: "all 0.3s ease"
                          }}></div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              <div style={{
                textAlign: "center",
                fontSize: "14px",
                fontWeight: "600",
                color: colors.secondaryText,
                lineHeight: "1.4"
              }}>
                {valueUsedForWorkout >= 3
                  ? "Daily Workout Completed! Great job! 🚀"
                  : (3 - valueUsedForWorkout === 1
                    ? "One more game needed to complete today's workout"
                    : `${3 - valueUsedForWorkout === 2 ? 'Two' : 3 - valueUsedForWorkout} more games needed to complete today's workout`)
                }
              </div>
            </div>
          </div>
        );
      })()}

      {/* WORDS REVIEW CARD */}
      <div style={{ width: "100%", maxWidth: "420px", padding: "0 12px 0", marginTop: "16px" }}>
        <div style={{ background: colors.surface, borderRadius: "24px", padding: "20px", boxShadow: isDark ? "0 10px 25px rgba(0,0,0,0.2)" : "0 10px 25px rgba(15,23,42,0.05)", border: isDark ? "1px solid #334155" : "none" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "20px" }}>📋</span>
              <span style={{ fontSize: "15px", fontWeight: "700", color: colors.text }}>Words Review</span>
            </div>
            <div style={{ display: "flex", background: isDark ? "#0F172A" : "#F8FAFC", padding: "4px", borderRadius: "12px", gap: "4px" }}>
              {["all", "correct", "wrong"].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: "6px 12px",
                    background: filter === f ? (isDark ? "rgba(20, 184, 166, 0.2)" : "#E0F2F1") : "transparent",
                    color: filter === f ? "#14B8A6" : colors.secondaryText,
                    borderRadius: "8px", border: "none", fontSize: "12px",
                    fontWeight: filter === f ? "700" : "600", cursor: "pointer",
                    textTransform: "capitalize"
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {filteredWords.map((w, i) => {
              const isCorrect = w.status === "correct";
              const isWrong = w.status === "wrong";

              return (
                <div key={i} style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  borderBottom: i === filteredWords.length - 1 ? "none" : `1px solid ${isDark ? "#334155" : "#F1F5F9"}`,
                  paddingBottom: i === filteredWords.length - 1 ? "0" : "16px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                    <div style={{
                      width: "32px", height: "32px", borderRadius: "50%",
                      background: isCorrect ? (isDark ? "rgba(74, 222, 128, 0.15)" : "#E8F5E9") : isWrong ? (isDark ? "rgba(244, 67, 54, 0.15)" : "#FFEBEE") : (isDark ? "#0F172A" : "#F1F5F9"),
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                    }}>
                      {isCorrect && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      )}
                      {isWrong && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F44336" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      {typeof w.word === 'object' && w.word.isTwins ? (
                        <>
                          <div style={{
                            fontSize: "15px",
                            fontWeight: "700",
                            color: colors.text,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}>
                            {w.word.word1} 
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 8L22 12L18 16" /><path d="M6 8L2 12L6 16" /><line x1="2" y1="12" x2="22" y2="12" />
                            </svg> 
                            {w.word.word2}
                          </div>
                          <div style={{
                            fontSize: "12px",
                            color: colors.secondaryText,
                            marginTop: "2px",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                          }}>
                            {w.word.meaning1} / {w.word.meaning2}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{
                            fontSize: "15px",
                            fontWeight: "700",
                            color: colors.text
                          }}>{w.word}</div>
                          <div style={{
                            fontSize: "12px",
                            color: colors.secondaryText,
                            marginTop: "2px",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                          }}>
                            {w.meaning}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {(gameType === "connotation" || gameType === "connotations") && (w.connotation === "strongly_positive" || w.connotation === "strongly_negative") && (
                    <div style={{
                      padding: "4px 8px",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: w.connotation === "strongly_positive" ? "#10B981" : "#EF4444",
                      background: w.connotation === "strongly_positive" ? (isDark ? "rgba(16, 185, 129, 0.15)" : "#F0FDF4") : (isDark ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2"),
                      border: `1px solid ${w.connotation === "strongly_positive" ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)"}`,
                      flexShrink: 0,
                      textAlign: "center",
                      minWidth: "70px"
                    }}>
                      {w.connotation === "strongly_positive" ? "Positive" : "Negative"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* BOTTOM ACTIONS */}
      <div style={{
        width: "100%", maxWidth: "420px",
        padding: "0 20px 40px",
        marginTop: "16px",
        display: "flex",
        gap: "12px",
        zIndex: 100
      }}>
        <button
          onClick={handleShare}
          style={{
            flex: 1, height: "56px", borderRadius: "18px", 
            border: isDark ? "1px solid #334155" : "1px solid #E2E8F0",
            background: colors.surface, color: colors.text, fontSize: "16px", fontWeight: "700",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "pointer"
          }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
          Share Score
        </button>
        <button
          onClick={() => setStep("input")}
          style={{
            flex: 1.5, height: "56px", borderRadius: "18px", border: "none",
            background: "#14B8A6", color: "#FFFFFF", fontSize: "16px", fontWeight: "700",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            boxShadow: isDark ? "0 10px 20px rgba(0,0,0,0.3)" : "0 10px 20px rgba(20,184,166,0.25)", cursor: "pointer"
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
          Play Again
        </button>
      </div>
    </div>
  );
};

export default ResultScreen;
